import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { IconButton, Menu, MenuItem, Link } from '@mui/material';
import React, { useState } from 'react';
import { Stack, styled, Tooltip } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';

export interface ObituaryHeaderOptionsProps {
  funeralHomeUrl: string;
  photoUrl: string;
  personUrl: string;
  personName: string;
}

const StyledLink = styled(Link)(({ theme }) => ({
  color: '#616161',
  padding: '12px',
  borderRadius: '50%',
  border: '1px',
  borderColor: theme.palette.barChart.main,
  margin: '0 0 0 20px',
  display: 'inline-block',
  maxWidth: '100px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  transition: 'all 0.3s',
}));

const StyledMenuItemMobile = styled(MenuItem)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
  },
}));

const StyledLinkMobile = styled(Link)(({ theme }) => ({
  textDecoration: 'none',

  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
  },
}));

export function ObituaryHeaderOptions({
  funeralHomeUrl,
  photoUrl,
  personUrl,
  personName,
}: ObituaryHeaderOptionsProps) {
  const { isMobile } = useScreenSize();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderLinks = () => (
    <>
      {funeralHomeUrl && (
        <Tooltip
          title="Funeral Home Website"
          children={
            <StyledLink
              href={funeralHomeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <InsertDriveFileIcon />
            </StyledLink>
          }
          placement="top"
        />
      )}
      {personUrl && (
        <Tooltip
          title="Obituary Page Link"
          children={
            <StyledLink
              href={personUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <InsertLinkIcon />
            </StyledLink>
          }
          placement="top"
        />
      )}
      {photoUrl && (
        <Tooltip
          title={`Photos of ${personName.split(' ')[0]}`}
          children={
            <StyledLink
              href={photoUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PhotoLibraryIcon />
            </StyledLink>
          }
          placement="top"
        />
      )}
    </>
  );

  return (
    <Stack direction="row" justifyContent="flex-end">
      {isMobile ? (
        <>
          <IconButton
            aria-label="more options"
            aria-controls="ellipsis-menu"
            aria-haspopup="true"
            onClick={handleClick}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="ellipsis-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {funeralHomeUrl && (
              <StyledMenuItemMobile onClick={handleClose}>
                <StyledLinkMobile href={funeralHomeUrl}>
                  Funeral Home Website
                </StyledLinkMobile>
              </StyledMenuItemMobile>
            )}
            {personUrl && (
              <StyledMenuItemMobile onClick={handleClose}>
                <StyledLinkMobile href={personUrl}>
                  Obituary Page Link
                </StyledLinkMobile>
              </StyledMenuItemMobile>
            )}
            {photoUrl && (
              <StyledMenuItemMobile onClick={handleClose}>
                <StyledLinkMobile href={photoUrl}>
                  Photos of {personName.split(' ')[0]}
                </StyledLinkMobile>
              </StyledMenuItemMobile>
            )}
          </Menu>
        </>
      ) : (
        renderLinks()
      )}
    </Stack>
  );
}
